import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit } from "@angular/core";
import { Item } from "../../../../pojo/Item";
import { S25Util } from "../../../../util/s25-util";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25Datefilter } from "../../../s25-dateformat/s25.datefilter.service";
import { OccurrenceModel, ReservationI } from "../../../../pojo/ReservationI";
import { S25ItemI } from "../../../../pojo/S25ItemI";
import { OccurrenceI } from "../../../../pojo/OccurrenceI";

interface Reservation {
    date: string;
    start: string;
    end: string;
    resources: { resource_id: string | number }[];
    spaces: { space_id: string | number }[];
    formData: ReservationI;
}

interface DateMap {
    [key: string]: number;
}

@TypeManagerDecorator("s25-ng-occurrences-config")
@Component({
    selector: "s25-ng-occurrences-config",
    template: `@if (init) {
        <div
            class="occurrence-config--wrapper c-margin-bottom--single c-margin-top--single"
            [attr.aria-label]="segmentLabel ? 'Configure Occurrences for ' + segmentLabel : 'Configure Occurrences'"
            tabindex="0"
        >
            @if (segmentLabel) {
                <div class="segment-label ngBold c-margin-bottom--half">{{ segmentLabel }}</div>
            }
            <div class="flex-form--wrapper">
                <div class="start-date-container">
                    <label class="c-margin-bottom--quarter c-margin-top--half" [attr.for]="startDateInputId"
                        >Start Date</label
                    >
                    <s25-datepicker
                        [(value)]="startDate"
                        [inputId]="startDateInputId"
                        [showToday]="true"
                        (valueChange)="updateStartDate()"
                    />
                    <label class="occurrence-header c-margin-top--half" [attr.for]="mappingSelectId">
                        Occurrence Mapping
                    </label>
                    <select
                        class="cn-form__control"
                        [(ngModel)]="mapping"
                        (ngModelChange)="updateMapping()"
                        [id]="mappingSelectId"
                        [name]="mappingSelectId"
                    >
                        <option value="one-by-one">One-to-one</option>
                        <option value="date-by-date">Date-to-date</option>
                        <option value="none">No Mapping</option>
                    </select>
                </div>
                <div class="form-separator"></div>
                <div class="occurrence-view" tabindex="0">
                    @for (res of reservations; track res; let i = $index) {
                        <div class="original-occurrence" [ngClass]="{ 'date-mapped': mapping === 'date-by-date' }">
                            <div class="occurrence-date" [ngClass]="{ mapped: showMapping }">
                                <div>{{ res.date }}</div>
                                @if (showMapping && mapping !== "date-by-date") {
                                    <s25-ng-icon [type]="'arrowRightDown'" [ariaLabel]="'maps to'" />
                                }
                                @if (showMapping && mapping !== "date-by-date") {
                                    <span [ngClass]="{ 'multi-line': isMultipleDays }">{{ occurrences[i].date }}</span>
                                }
                            </div>
                            <div class="occurrence-times">
                                <div>{{ res.start }}</div>
                                <div>{{ res.end }}</div>
                            </div>
                            <div class="occurrence-objects">
                                @if (mapping === "one-by-one" || mapping === "date-by-date") {
                                    @if (!res.spaces?.length && !res.resources?.length) {
                                        <div class="no-details">No Additional Details Found</div>
                                    }
                                    @for (space of res.spaces; track space) {
                                        <div>
                                            <s25-item
                                                [itemId]="space.space_id"
                                                [hideLabel]="false"
                                                [itemTypeId]="Item.Ids.Location"
                                                [includeTypeIcon]="true"
                                            ></s25-item>
                                        </div>
                                    }
                                    @for (resource of res.resources; track resource) {
                                        <div>
                                            <s25-item
                                                [itemId]="resource.resource_id"
                                                [itemTypeId]="Item.Ids.Resource"
                                                [includeTypeIcon]="true"
                                            ></s25-item>
                                        </div>
                                    }
                                }
                                @if (mapping === "none") {
                                    @if (!reservations[0].spaces && !reservations[0].resources) {
                                        <div class="no-details">No Additional Details Found</div>
                                    }
                                    @for (space of allLocations; track space) {
                                        <div>
                                            <s25-item
                                                [itemId]="space.itemId"
                                                [hideLabel]="false"
                                                [itemTypeId]="Item.Ids.Location"
                                                [includeTypeIcon]="true"
                                            ></s25-item>
                                        </div>
                                    }
                                    @for (resource of allResources; track resource) {
                                        <div>
                                            <s25-item
                                                [itemId]="resource.itemId"
                                                [itemTypeId]="Item.Ids.Resource"
                                                [includeTypeIcon]="true"
                                            ></s25-item>
                                        </div>
                                    }
                                }
                            </div>
                            @if (showMapping && mapping === "date-by-date") {
                                <div class="mapped-occurrence">
                                    <s25-ng-icon [type]="'arrowRightDown'" [ariaLabel]="'Expand'" />
                                    <div class="occurrence-date">{{ occurrences[i].date }}</div>
                                    <div class="occurrence-times">
                                        <div>{{ occurrences[i].start }}</div>
                                        <div>{{ occurrences[i].end }}</div>
                                    </div>
                                    <div
                                        class="occurrence-objects"
                                        [ngClass]="{
                                            'no-details': !occurrences[i].spaces && !occurrences[i].resources,
                                        }"
                                    >
                                        @if (!occurrences[i].spaces && !occurrences[i].resources) {
                                            <div class="no-details">No Additional Details Found</div>
                                        }
                                        @for (space of occurrences[i].spaces; track space) {
                                            <div>
                                                <s25-item
                                                    [itemId]="space.space_id"
                                                    [hideLabel]="false"
                                                    [itemTypeId]="Item.Ids.Location"
                                                    [includeTypeIcon]="true"
                                                ></s25-item>
                                            </div>
                                        }
                                        @for (resource of occurrences[i].resources; track resource) {
                                            <div>
                                                <s25-item
                                                    [itemId]="resource.resource_id"
                                                    [itemTypeId]="Item.Ids.Resource"
                                                    [includeTypeIcon]="true"
                                                ></s25-item>
                                            </div>
                                        }
                                    </div>
                                </div>
                            }
                            @if (occurrences.length > 1) {
                                <div>
                                    <span class="ngBold ngBlock remove-mapping">
                                        <input
                                            type="checkbox"
                                            [id]="removeMappingInputId + '-' + i"
                                            [checked]="isRemovedIndex(i)"
                                            (change)="onRemoveMappingDate($event, i)"
                                        />
                                        Remove this mapping date
                                    </span>
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>
        </div>
    }`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25OccurrencesConfigComponent implements OnInit {
    @Input() eventId: number;
    @Input() dateFormat: string;
    @Input() timeFormat: string;
    @Input() initialOccurrences?: ReservationI[];
    @Input() profileInitStartDt: string;
    @Input() profileCode?: string;
    @Input() segmentLabel?: string;
    @Input() onChange: (data: OccurrenceModel[]) => void;

    private static nextId = 0;

    init: boolean = false;
    mapping: OccurrenceI.Mapping = "one-by-one";
    startDate: Date;
    startDateInputId = `startDateContainer-${S25OccurrencesConfigComponent.nextId++}`;
    mappingSelectId = `occurrenceDetails-${S25OccurrencesConfigComponent.nextId++}`;
    removeMappingInputId = `removeMappingDate-${S25OccurrencesConfigComponent.nextId++}`;
    occurrences: Reservation[] = []; // temporary occurrence reservations based on current mapping/date selections for displaying on visual aid
    reservations: Reservation[]; // occurrence reservations from original event
    showMapping: boolean;
    dateMap: DateMap = {};
    isMultipleDays: boolean;
    diffDays: number = 0;
    occurrenceModel: OccurrenceModel[] = []; // final occurrence data sent to the event form on submit
    removedMappingIndexes = new Set<number>();
    allResources: S25ItemI[] = [];
    allLocations: S25ItemI[] = [];

    protected readonly Item = Item;

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this;
    }

    ngOnInit() {
        this.startDate = new Date(this.profileInitStartDt);

        this.reservations = this.getReservations() ?? [];

        this.onOccChange();

        this.init = true;

        this.cd.detectChanges();
    }

    updateStartDate() {
        this.diffDays = S25Util.date.diffDays(this.profileInitStartDt, this.startDate);

        if (this.diffDays !== 0) {
            this.occurrenceModel = [];

            this.occurrences = this.reservations.map((res: Reservation) => {
                let dates;
                if (this.isMultipleDays) {
                    dates = res.date
                        .split(" - ")
                        .map((date: string) => {
                            return S25Datefilter.transform(S25Util.date.addDays(date, this.diffDays), this.dateFormat);
                        })
                        .join(" - ");
                }

                this.occurrenceModel.push({
                    startDt: S25Util.date.addDays(res.formData.startDt, this.diffDays),
                    endDt: S25Util.date.addDays(res.formData.endDt, this.diffDays),
                    locations: res.formData.space ? [...res.formData.space] : [],
                    resources: res.formData.res ? [...res.formData.res] : [],
                    rsrvId: res.formData.rsvId,
                });

                return {
                    date:
                        dates ||
                        S25Datefilter.transform(S25Util.date.addDays(res.date, this.diffDays), this.dateFormat),
                    start: res.start,
                    end: res.end,
                    resources: res.resources,
                    spaces: res.spaces,
                    formData: {
                        startDt: S25Util.date.addDays(res.formData.startDt, this.diffDays),
                        endDt: S25Util.date.addDays(res.formData.endDt, this.diffDays),
                        state: res.formData.state,
                        rsvId: res.formData.rsvId,
                        locations: res.formData.space ? [...res.formData.space] : [],
                        resources: res.formData.res ? [...res.formData.res] : [],
                    },
                };
            });

            this.showMapping ||= true;
            this.updateMapping();
            return;
        }

        this.showMapping &&= false;
        this.updateMapping();
    }

    updateMapping() {
        if (
            this.mapping === "date-by-date" &&
            this.occurrences[0] &&
            S25Util.date.isBetweenEqual(
                this.occurrences[0].date,
                this.reservations[0].date,
                this.reservations[this.reservations.length - 1].date,
            )
        ) {
            this.occurrences.forEach((occ: Reservation, i) => {
                const resData = this.dateMap[occ.date]
                    ? this.reservations[this.dateMap[occ.date]]
                    : this.reservations[0];

                occ.start = resData.start;
                occ.end = resData.end;
                occ.resources = resData.resources ? [...resData.resources] : [];
                occ.spaces = resData.spaces ? [...resData.spaces] : [];

                this.occurrenceModel[i].locations = resData.formData.space ? [...resData.formData.space] : [];
                this.occurrenceModel[i].resources = resData.formData.res ? [...resData.formData.res] : [];
            });
        } else if (this.mapping === "none") {
            if (this.diffDays === 0) {
                this.occurrenceModel = [];

                this.reservations.forEach((res: Reservation) => {
                    this.occurrenceModel.push({
                        startDt: res.formData.startDt,
                        endDt: res.formData.endDt,
                        locations: [...this.allLocations],
                        resources: [...this.allResources],
                    });
                });
            } else if (this.occurrenceModel.length && this.occurrenceModel.length > 0) {
                this.occurrenceModel.forEach((occ: OccurrenceModel) => {
                    occ.locations = [...this.allLocations];
                    occ.resources = [...this.allResources];
                });
            }
        }
        this.onOccChange();

        this.cd.detectChanges();
    }

    getReservations(): Reservation[] | undefined {
        return this.initialOccurrences?.map((rsrv: ReservationI, i: number): Reservation => {
            this.dateMap[S25Datefilter.transform(rsrv.startDt, this.dateFormat)!] = i;

            const isMultipleDays =
                S25Datefilter.transform(rsrv.startDt, this.dateFormat) !==
                S25Datefilter.transform(rsrv.endDt, this.dateFormat);
            const dateRange =
                isMultipleDays &&
                `${S25Datefilter.transform(rsrv.startDt, this.dateFormat)} - ${S25Datefilter.transform(
                    rsrv.endDt,
                    this.dateFormat,
                )}`;
            this.isMultipleDays = isMultipleDays;

            this.allResources = [...this.allResources];
            rsrv.res?.forEach((resource) => {
                const found = this.allResources.find((el) => el.itemId === resource.itemId);
                if (!found) this.allResources.push(resource);
            });

            this.allLocations = [...this.allLocations];
            rsrv.space?.forEach((space) => {
                const found = this.allLocations.find((el) => el.itemId === space.itemId);
                if (!found) this.allLocations.push(space);
            });

            this.occurrenceModel.push({
                startDt: rsrv.startDt,
                endDt: rsrv.endDt,
                locations: rsrv.space,
                resources: rsrv.res,
            });

            return {
                date: dateRange || S25Datefilter.transform(rsrv.startDt, this.dateFormat)!,
                start: S25Datefilter.transform(rsrv.startDt, this.timeFormat)!,
                end: S25Datefilter.transform(rsrv.endDt, this.timeFormat)!,
                resources:
                    rsrv.res
                        ?.map((res) => res.itemId)
                        .filter(S25Util.isDefined)
                        .map((id) => ({ resource_id: id })) ?? [],
                spaces:
                    rsrv.space
                        ?.map((space) => space.itemId)
                        .filter(S25Util.isDefined)
                        .map((id) => ({ space_id: id })) ?? [],
                formData: rsrv,
            };
        });
    }

    onOccChange() {
        let profileCode;
        if (this.removedMappingIndexes.size > 0) {
            profileCode = "adhoc";
        } else if (this.diffDays !== 0) {
            if (this.profileCode?.[0] === "W") {
                const profileCodeParts = this.profileCode.split(" ");
                const oldScheme = profileCodeParts.slice(1, -1);

                const newScheme = [];
                for (let i = 0; i < oldScheme.length; i++) {
                    newScheme.push(this.occurrenceModel[i].startDt?.toString().slice(0, 2).toUpperCase());
                }

                profileCode = [
                    ...profileCodeParts.slice(0, 1),
                    ...newScheme,
                    ...profileCodeParts.slice(newScheme.length + 1),
                ].join(" ");
            }
        }

        const activeOccurrences = this.occurrenceModel.filter(
            (_occurrence, index) => !this.removedMappingIndexes.has(index),
        );
        if (!activeOccurrences.length) {
            return;
        }

        activeOccurrences[0].profile_code = profileCode ?? this.profileCode ?? "adhoc";
        activeOccurrences[0].eventId = this.eventId;
        activeOccurrences[0].mapping = this.mapping;

        this.onChange(activeOccurrences);
    }

    isRemovedIndex(index: number): boolean {
        return this.removedMappingIndexes.has(index);
    }

    onRemoveMappingDate(event: Event, index: number) {
        const checked = (event.target as HTMLInputElement).checked;
        if (checked) {
            this.removedMappingIndexes.add(index);
        } else {
            this.removedMappingIndexes.delete(index);
        }
        this.onOccChange();
        this.cd.detectChanges();
    }
}
